import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Alert, Button, Card, Empty, Popconfirm, Spin, Tag } from 'antd';
import { getAuthHeaders } from '../utils/authHeaders';
import { ATTICUS_BROWSER_API, atticusError } from './contracts';

interface Connection {
  id: string;
  provider: string;
  revoked_at?: string | null;
}
export default function AtticusConnections({
  userId,
  isAuth,
}: {
  userId: string;
  isAuth: boolean;
}): JSX.Element {
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const ownerVersion = useRef(0);
  useEffect(() => {
    ownerVersion.current += 1;
    setConnections([]);
    setError(null);
    setBusy(null);
  }, [userId, isAuth]);
  const load = useCallback(async () => {
    if (!isAuth || !userId) {
      setLoading(false);
      return;
    }
    const version = ownerVersion.current;
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get<{ connections: Connection[] }>(
        `${ATTICUS_BROWSER_API}/connections`,
        { headers, withCredentials: true }
      );
      if (!Array.isArray(response.data?.connections)) throw new Error('Connections unavailable.');
      if (version === ownerVersion.current)
        setConnections(response.data.connections.filter((item) => item.provider === 'atticus'));
    } catch (error) {
      if (version === ownerVersion.current) setError(atticusError(error));
    } finally {
      if (version === ownerVersion.current) setLoading(false);
    }
  }, [isAuth, userId]);
  useEffect(() => {
    void load();
  }, [load]);
  const disconnect = async (connection: Connection) => {
    const version = ownerVersion.current;
    setBusy(connection.id);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      await axios.delete(
        `${ATTICUS_BROWSER_API}/connections/${encodeURIComponent(connection.id)}`,
        { headers, withCredentials: true }
      );
      if (version === ownerVersion.current)
        setConnections((current) =>
          current.map((item) =>
            item.id === connection.id ? { ...item, revoked_at: new Date().toISOString() } : item
          )
        );
    } catch (error) {
      if (version === ownerVersion.current) setError(atticusError(error));
    } finally {
      if (version === ownerVersion.current) setBusy(null);
    }
  };
  return (
    <Card className="account-integrations-card" title="Atticus connections">
      <p>
        Connect from Atticus to send selected books for AI-assisted translation. Each translation
        requires your approval and uses your ScribeShadow credits.
      </p>
      {loading ? (
        <Spin tip="Loading Atticus connections…" />
      ) : error ? (
        <Alert
          type="error"
          message={error}
          action={<Button onClick={() => void load()}>Retry Atticus connections</Button>}
        />
      ) : connections.length === 0 ? (
        <Empty description="No Atticus connections" />
      ) : (
        connections.map((connection) => (
          <div key={connection.id} className="account-integrations-setting-row">
            <span>
              Atticus <Tag>{connection.revoked_at ? 'Disconnected' : 'Connected'}</Tag>
            </span>
            <Popconfirm
              title="Disconnect Atticus?"
              description="Atticus loses access to new requests and results. Accepted translations continue in ScribeShadow."
              okText="Disconnect"
              onConfirm={() => void disconnect(connection)}
            >
              <Button
                danger
                disabled={Boolean(connection.revoked_at) || Boolean(busy)}
                loading={busy === connection.id}
              >
                Disconnect Atticus
              </Button>
            </Popconfirm>
          </div>
        ))
      )}
      <p>
        Disconnecting blocks new access and result delivery. Already accepted translations continue
        under their recorded approval.
      </p>
    </Card>
  );
}
